import { NgClass, UpperCasePipe } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonComponent } from '@common/components/button/button.component';
import { TextInputComponent } from '@common/components/input/text-input/text-input.component';
import { SetEditModeDirective } from '@common/directives/set-edit-mode.directive';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { environment } from '@environments/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { DropDownButtonComponent } from '@progress/kendo-angular-buttons';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { DarkModeService } from '@services/dark-mode.service';
import { I18nService } from '@services/i18n.service';
import { QueryService } from '@services/query.service';
import { RememberStateService } from '@services/remember-state.service';
import { ToastrNotificationService } from '@services/toastr-notification.service';
import { UserService } from '@services/user.service';
import { distinctUntilChanged } from 'rxjs';

@Component({
    templateUrl: './login.component.html',
    imports: [
        ButtonComponent,
        DropDownButtonComponent,
        TranslatePipe,
        NgClass,
        UpperCasePipe,
        TooltipDirective,
        FormsModule,
        SetEditModeDirective,
        TextInputComponent
    ]
})
export class LoginComponent implements OnInit, AfterViewInit {
    username: string;
    password: string;
    errorMessage: string;
    currentYear = new Date().getFullYear();
    isBusy = false;
    environment = environment.name;
    settings = environment.settings;
    environmentColor = environment.settings.header.environmentColor;
    title = environment.title;
    logo = environment.settings.header.logo;
    version = environment.version;
    showTermsAndConditions = false;
    showRegister = false;
    showForgetPassword = false;
    showLanguageFlag = false;
    certificateLogin = false;
    googleLogin = false;
    appleLogin = false;
    facebookLogin = false;
    private i18nService = inject(I18nService);
    currentLanguage = this.i18nService.currentLanguage;
    languages = this.i18nService.languages;
    private userService = inject(UserService);
    private router = inject(Router);
    private rememberStateService = inject(RememberStateService);
    private darkModeService = inject(DarkModeService);
    private toastrNotificationService = inject(ToastrNotificationService);
    private queryService = inject(QueryService);

    ngOnInit(): void {
        this.userService.isAuthenticatedSubject.pipe(distinctUntilChanged()).subscribe((isAuth: boolean) => {
            if (isAuth) {
                this.router.navigateByUrl('/');
                return;
            }
        });
        this.rememberStateService.clear(['languageId', 'darkMode']);
    }

    ngAfterViewInit() {
        this.getAppSettings();
    }

    toggleTheme() {
        this.darkModeService.toggle();
    }

    onSubmit() {
        this.signIn();
    }

    isDarkTheme() {
        return this.darkModeService.get();
    }

    private async signIn() {
        this.isBusy = true;
        this.errorMessage = null;
        const returnUrl = this.router.routerState.snapshot.root.queryParams.returnUrl ?? '/';

        if (!this.username || !this.password) {
            this.toastrNotificationService.show({
                type: 'error',
                message: 'Username and Password are required fields',
                title: 'Error'
            });
            this.isBusy = false;
        } else {
            await this.userService
                .login(this.username, this.password)
                .then(async (success: boolean) => {
                    if (!success) return;
                    await this.router.navigateByUrl(returnUrl);
                    await this.getActiveNotifications();
                    await this.queryService.queryClient.invalidateQueries({ queryKey: ['settings'] });
                })
                .catch((errorMessage) => (this.errorMessage = errorMessage))
                .finally(() => (this.isBusy = false));
        }
    }

    private async getActiveNotifications() {
        const res = await this.queryService.query('HasActiveNotifications', { important: true });
        if (!res) return;
        this.toastrNotificationService
            .show(
                {
                    type: 'info',
                    message: 'You have unread notifications. Click here to view them.',
                    title: 'Notifications'
                },
                {
                    timeOut: 10_000,
                    closeButton: true,
                    tapToDismiss: false,
                    progressBar: true
                }
            )
            .onTap.subscribe(() => {
                this.router.navigate(['/notice-board/notification/list'], {
                    queryParams: { tab: 'All' }
                });
            });
    }

    private getAppSettings() {
        this.queryService.queryClient.ensureQueryData({ queryKey: ['loginSettings'] }).then((result: Setting[]) => {
            this.showTermsAndConditions = this.getSettingValueBoolean(result, SettingKeyCodes.TermsAndConditions);
            this.showRegister = this.getSettingValueBoolean(result, SettingKeyCodes.UserRegistration);
            this.showForgetPassword = this.getSettingValueBoolean(result, SettingKeyCodes.ForgetPassword);
            this.showLanguageFlag = this.getSettingValueBoolean(result, SettingKeyCodes.LanguageFlag);
            this.certificateLogin = this.getSettingValueBoolean(result, SettingKeyCodes.CertificateLogin);
            this.googleLogin = this.getSettingValueBoolean(result, SettingKeyCodes.GoogleLogin);
            this.appleLogin = this.getSettingValueBoolean(result, SettingKeyCodes.AppleLogin);
            this.facebookLogin = this.getSettingValueBoolean(result, SettingKeyCodes.FacebookLogin);
            this.title = this.getSettingValue(result, SettingKeyCodes.LongCompanyName);
            const companyLogo = result?.find((s: Setting) => s.key === SettingKeyCodes.Logo);
            if (companyLogo) this.handleLogo(companyLogo.value);
        });
    }

    private getSettingValue(result: Setting[], settingKey: SettingKeyCodes): string | undefined {
        const setting = result?.find((s: Setting) => s.key === settingKey);
        return setting?.value;
    }

    private getSettingValueBoolean(result: Setting[], settingKey: SettingKeyCodes): boolean {
        const value = this.getSettingValue(result, settingKey);
        return value === 'true';
    }

    private handleLogo(imageContent: string) {
        const decodedData = atob(imageContent);
        const uint8Array = new Uint8Array(decodedData.length);
        for (let i = 0; i < decodedData.length; ++i) {
            uint8Array[i] = decodedData.charCodeAt(i);
        }
        const blob = new Blob([uint8Array]);
        this.logo = URL.createObjectURL(blob);
    }

    requestPasswordReset() {
        if (!this.username) {
            this.toastrNotificationService.show({
                type: 'warning',
                message: "Please enter your username and press 'Forgot password' button again.",
                title: 'Warning'
            });
            return;
        }

        this.userService
            .forgetPassword(this.username)
            .then(() => {
                this.toastrNotificationService.show({
                    type: 'success',
                    message: 'You will receive a password reset link shortly. Please check your inbox.',
                    title: 'Success'
                });
            })
            .catch((error) => {
                this.toastrNotificationService.show({
                    type: 'error',
                    message: error,
                    title: 'Error'
                });
            });
    }
}
